<template>
  <div v-if="showBanner" class="main-pc-status" :class="statusClass">
    <span class="main-pc-status__text">
      {{ bannerText }}
    </span>
    <button
      v-if="showDismissButton"
      type="button"
      class="main-pc-status__close-btn"
      aria-label="Close warning"
      @click="dismissBanner"
    >
      x
    </button>
    <button
      v-if="showActionButton"
      type="button"
      class="main-pc-status__btn"
      @click="handleActionClick"
      :disabled="isCheckingConnection"
    >
      {{ actionButtonText }}
    </button>
  </div>
</template>

<script>
import { Storage } from '@/services/Storage';
import tableServerClient from '@/services/tableServerClient';
import Swal from 'sweetalert2';
import { showPasswordPrompt } from '@/utils/passwordUtils';
import { getPlatform } from '@/utils/platformDetection';

export default {
  name: 'HardwareServiceChecker',
  data() {
    return {
      hasMainPcSet: true,
      canReachMainPc: true,
      isStoreSelected: false,
      isMainPc: false,
      requiresMainPc: true,
      isCheckingConnection: false,
      showConnectedNotice: false,
      dismissedDisconnectedBanner: false,
      connectedNoticeTimeout: null,
      checkInterval: null,
    };
  },
  computed: {
    showBanner() {
      if (this.$route?.name === 'main-table-server-config') return false;
      if (!this.isStoreSelected) return false;
      if (!this.requiresMainPc) return false;
      if (!this.hasMainPcSet) return true;
      if (this.isMainPc) return false;
      if (!this.canReachMainPc) return !this.dismissedDisconnectedBanner;
      return this.showConnectedNotice;
    },
    statusClass() {
      if (!this.hasMainPcSet) return 'main-pc-status--missing';
      if (!this.canReachMainPc) return 'main-pc-status--disconnected';
      return 'main-pc-status--connected';
    },
    bannerText() {
      if (!this.hasMainPcSet) {
        return 'Main PC is not configured. Set the Main PC in Table Server Settings.';
      }
      if (!this.canReachMainPc) {
        return 'Cannot reach Main PC. Check network/Main PC and try reconnect.';
      }
      return 'Connected to Main PC.';
    },
    showActionButton() {
      return !this.hasMainPcSet || !this.canReachMainPc;
    },
    showDismissButton() {
      return this.hasMainPcSet && !this.canReachMainPc;
    },
    actionButtonText() {
      if (!this.hasMainPcSet) return 'Set Main PC';
      return this.isCheckingConnection ? 'Reconnecting...' : 'Retry Connection';
    },
  },
  methods: {
    isAndroid() {
      return getPlatform() === 'android' || !!window.androidMode;
    },
    async checkMainPcConfig() {
      const wasReachable = this.canReachMainPc;
      try {
        const store = await Storage.getDefaultStore();
        this.isStoreSelected = !!(store && store.StoreDB);

        if (!this.isStoreSelected) {
          this.hasMainPcSet = true;
          this.canReachMainPc = true;
          this.requiresMainPc = true;
          this.isMainPc = false;
          this.showConnectedNotice = false;
          this.dismissedDisconnectedBanner = false;
          this.clearConnectedNotice();
          return;
        }

        // Binary: is Main, or can reach Main (same probe as Settings / create table).
        this.isCheckingConnection = true;
        const result = await tableServerClient.checkConnection();
        this.isMainPc = !!result.isMain;
        this.requiresMainPc = !this.isMainPc;
        const target = String(result.target || '').trim();
        this.hasMainPcSet = this.isMainPc || (target.length > 0 && target !== 'This device');
        this.canReachMainPc = this.isMainPc || !!result.connected;

        if (this.isMainPc) {
          this.showConnectedNotice = false;
          this.dismissedDisconnectedBanner = false;
          this.clearConnectedNotice();
        }
      } catch (error) {
        // Avoid false "Set Main PC" popups on transient read/connectivity errors.
        this.canReachMainPc = false;
        console.warn('[HardwareServiceChecker] Main PC status check failed:', error);
      } finally {
        if (this.canReachMainPc) {
          this.dismissedDisconnectedBanner = false;
        } else if (wasReachable && !this.canReachMainPc && this.hasMainPcSet) {
          this.dismissedDisconnectedBanner = false;
        }
        this.isCheckingConnection = false;
      }
    },
    goToTableServerSettings() {
      if (this.$route?.name === 'main-table-server-config') return;
      this.requestAdvancedAccess().then((allowed) => {
        if (allowed) {
          this.$router.push({ name: 'main-table-server-config' });
        }
      });
    },
    async requestAdvancedAccess() {
      const password = await showPasswordPrompt(
        'Administrator Access',
        'Enter password to access advanced settings'
      );

      if (!password) return false;

      Swal.fire({
        icon: 'success',
        title: 'Access Granted',
        text: 'You now have access to advanced settings',
        timer: 2000,
        showConfirmButton: false,
        timerProgressBar: true,
        position: 'top-end',
        toast: true
      });
      return true;
    },
    clearConnectedNotice() {
      if (this.connectedNoticeTimeout) {
        clearTimeout(this.connectedNoticeTimeout);
        this.connectedNoticeTimeout = null;
      }
    },
    setConnectedNotice() {
      this.showConnectedNotice = true;
      this.clearConnectedNotice();
      this.connectedNoticeTimeout = setTimeout(() => {
        this.showConnectedNotice = false;
      }, 8000);
    },
    async retryConnection() {
      this.showConnectedNotice = false;
      await this.checkMainPcConfig();
      if (this.hasMainPcSet && this.canReachMainPc && !this.isMainPc) {
        this.setConnectedNotice();
      }
    },
    handleActionClick() {
      if (!this.hasMainPcSet) {
        this.goToTableServerSettings();
        return;
      }
      this.retryConnection();
    },
    dismissBanner() {
      if (this.hasMainPcSet && !this.canReachMainPc) {
        this.dismissedDisconnectedBanner = true;
      }
    },
    startChecking() {
      this.stopChecking();
      this.checkInterval = setInterval(() => {
        this.checkMainPcConfig();
      }, 5000);
    },
    stopChecking() {
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    },
    handleWindowFocus() {
      this.checkMainPcConfig();
    },
  },
  watch: {
    $route() {
      this.checkMainPcConfig();
    },
  },
  created() {
    this.checkMainPcConfig();
    this.startChecking();
    window.addEventListener('focus', this.handleWindowFocus);
  },
  beforeDestroy() {
    this.stopChecking();
    this.clearConnectedNotice();
    window.removeEventListener('focus', this.handleWindowFocus);
  },
};
</script>

<style scoped>
.main-pc-status {
  background-color: #c62828;
  color: #fff;
  padding: 10px 14px;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-sizing: border-box;
}

.main-pc-status--missing {
  background-color: #c62828;
}

.main-pc-status--disconnected {
  background-color: #ef6c00;
}

.main-pc-status--connected {
  background-color: #2e7d32;
}

.main-pc-status__text {
  font-size: 14px;
  line-height: 1.3;
}

.main-pc-status__btn {
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: transparent;
  color: #fff;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.main-pc-status__btn:disabled {
  opacity: 0.7;
  cursor: default;
}

.main-pc-status__btn:hover {
  background: rgba(255, 255, 255, 0.14);
}

.main-pc-status__close-btn {
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: transparent;
  color: #fff;
  border-radius: 6px;
  width: 30px;
  height: 30px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}

.main-pc-status__close-btn:hover {
  background: rgba(255, 255, 255, 0.14);
}

@media (max-width: 640px) {
  .main-pc-status {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
}
</style>
